import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextConsignment } from './Consignment';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../../../services/admin/consignment.service';
import { Error } from '../../../../models/error.model';
import { Consignment, ConsignmentLine } from '../../../../models/consignment.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppPreloader from '../../../../components/Preloader';

export interface ConsignmentReturnPageProps {};

let errorResponse: Error;

interface ReturnLine {
  id_variant: string;
  product_name: string;
  quantity_available: number;
  quantity_to_return: string;
}

const ConsignmentReturnPage: React.FunctionComponent<ConsignmentReturnPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextConsignment()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [consignment, setConsignment] = useState<Consignment | null>(null)
  const [returnLines, setReturnLines] = useState<ReturnLine[]>([])

  const loadConsignment = async (id_company: string, id_consignment: string) => {
    await ApiDigitalAdminConsignment.getConsignment(id_company, id_consignment).then( (response) => {
      if (response.status === 200) {
        let data: Consignment = response.data
        setConsignment(data)

        if (data.lines) {
          let availableLines = data.lines.filter((line: ConsignmentLine) => line.quantity_available > 0)
          setReturnLines(availableLines.map((line: ConsignmentLine) => ({
            id_variant: line.id_variant,
            product_name: line.product_name,
            quantity_available: line.quantity_available,
            quantity_to_return: '0'
          })))
        }
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangeQuantity = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    let newLines = [...returnLines]
    let value = parseInt(event.target.value) || 0
    if (value > newLines[index].quantity_available) value = newLines[index].quantity_available
    if (value < 0) value = 0
    newLines[index].quantity_to_return = value.toString()
    setReturnLines(newLines)
  }

  const submitReturn = async () => {
    setLoadIndicator('on')

    let linesToReturn = returnLines.filter(line => parseInt(line.quantity_to_return) > 0)

    if (companyForUser && consignment && linesToReturn.length > 0) {
      let payload = linesToReturn.map(line => ({
        id_variant: line.id_variant,
        quantity: parseInt(line.quantity_to_return)
      }))

      await ApiDigitalAdminConsignment.postConsignmentReturn(companyForUser.id, consignment.id, payload).then( (response) => {
        if (response.status === 201) {
          GlobalSwal.fire({
            title: lang.labels.successfullyCreated,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/purchase/consignment/view/' + consignment.id, {replace: true})
          })
        } else {
          errorResponse = response.data
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadConsignment(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.consignments, branch: lang.labels.returnProducts}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && consignment
        ?
        <div className="form">
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h2>{lang.labels.returnProducts}</h2>
              </div>
            </div>
            <div className="card-body pt-0">
              { returnLines.length > 0
                ?
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-150px">{lang.labels.product}</th>
                        <th className="w-120px text-end">{lang.labels.quantityAvailable}</th>
                        <th className="w-150px text-end">{lang.labels.quantityToReturn}</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { returnLines.map((line, index) => (
                        <tr key={index}>
                          <td>{line.product_name}</td>
                          <td className="text-end">{line.quantity_available}</td>
                          <td className="text-end">
                            <input className="form-control form-control-sm text-end" type="number" min="0" max={line.quantity_available} step="1" value={line.quantity_to_return} onChange={(e) => handleChangeQuantity(index, e)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                :
                <div className="text-center text-muted fw-bold py-10">
                  {lang.labels.noRecordsFound}
                </div>
              }
            </div>
          </div>
          { returnLines.length > 0 &&
            <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
              <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
                <div className="d-grid">
                  <span className="text-start text-dark fw-bolder fs-5">{lang.labels.returnProducts}</span>
                </div>
              </div>
              <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitReturn}>
                <span className="indicator-label text-uppercase text-nowrap">{lang.labels.returnProducts}</span>
                <span className="indicator-progress">
                  {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          }
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ConsignmentReturnPage;
